import React, { useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Grid,
} from '@mui/material'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import Alert from '@mui/material/Alert'
import PhoneIcon from '@mui/icons-material/Phone'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied'
import EventNoteIcon from '@mui/icons-material/EventNote'
import AssessmentIcon from '@mui/icons-material/Assessment'
import { gsap } from 'gsap'
import { useTranslation } from 'react-i18next'
import logger from '../../../utils/logger'
import { getCurrentDate } from '../../../services/date'

import AnalyticsMetricCardView from '../MetricCard'

import styles from './EnhancedMetricsSection.module.scss'
const MetricCard = AnalyticsMetricCardView

const EnhancedMetricsSection = ({
  metrics,
  metricsLoading
}) => {
  const { t } = useTranslation()
  const todayMetricsRef = useRef(null)
  const dateRangeMetricsRef = useRef(null)
  const monthMetricsRef = useRef(null)

  const isCssEnabled = false

  const metricsData = metrics || {}
  const metricsDateRange = {
    startDate: getCurrentDate(),
    endDate: getCurrentDate()
  }
  const dateRangeLoading = false

  const isTodaySelected = () => {
    const curDate = getCurrentDate().toISOString().split('T')[0]
    const startDate = metricsDateRange.startDate
    const endDate = metricsDateRange.endDate

    return (
      startDate.toISOString().split('T')[0] === curDate &&
      endDate.toISOString().split('T')[0] === curDate
    )
  }

  /* istanbul ignore next */
  useEffect(() => {
    if (!metricsLoading && metrics && todayMetricsRef.current && gsap) {
      try {
        if (typeof gsap.fromTo === 'function') {
          gsap.fromTo(
            todayMetricsRef.current.querySelectorAll('.metric-card'),
            { y: 50, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'power2.out' }
          )

          if (dateRangeMetricsRef.current) {
            gsap.fromTo(
              dateRangeMetricsRef.current.querySelectorAll('.metric-card'),
              { y: 50, opacity: 0 },
              { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'power2.out', delay: 0.3 }
            )
          }

          if (monthMetricsRef.current) {
            gsap.fromTo(
              monthMetricsRef.current.querySelectorAll('.metric-card'),
              { y: 50, opacity: 0 },
              { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'power2.out', delay: 0.6 }
            )
          }
        }
      } catch (error) {
        logger.warn('Animation error:', error)
      }

      if (typeof gsap.fromTo === 'function') {
        try {
          const metricValues = document.querySelectorAll('.metric-value-animate')
          metricValues.forEach((element) => {
            const targetValue = parseFloat(element.getAttribute('data-value'))
            if (!isNaN(targetValue)) {
              gsap.fromTo(
                element,
                { innerText: 0 },
                {
                  innerText: targetValue,
                  duration: 1.5,
                  ease: 'power2.out',
                  snap: { innerText: 0.1 },
                  onUpdate: function () {
                    if (element.getAttribute('data-decimals') === '0') {
                      element.innerText = Math.round(this.targets()[0].innerText)
                    } else {
                      element.innerText = parseFloat(this.targets()[0].innerText).toFixed(
                        parseInt(element.getAttribute('data-decimals') || '0')
                      )
                    }

                    if (element.getAttribute('data-percentage') === 'true') {
                      element.innerText += '%'
                    }
                  }
                }
              )
            }
          })
        } catch (error) {
          logger.warn('Number animation error:', error)
        }
      }
    }
  }, [metrics, metricsLoading])

  const showFullSkeleton = metricsLoading

  return (
    <>
      {showFullSkeleton ? (
        <Box>
          {/* Today's Metrics Section Skeleton */}
          <Box className={`${styles.sectionHeader} ${styles.sectionHeader}`}>
            <EventNoteIcon />
            <Typography variant='h6' component='h2' className={`${styles.sectionTitle} ${styles.sectionTitle}`}>
              {t('metrics.sections.todayMetrics')}
            </Typography>
          </Box>
          <Skeleton height={36} width={180} className={styles.skeletonSpacing} />

          {/* Today's Metrics Cards Skeletons */}
          <Grid container spacing={2} className={`${styles.metricsContainer} ${styles.metricsContainer}`}>
            <Grid item xs={12} sm={6} md={4} className={`${styles.gridItem} ${styles.gridItem}`}>
              <Skeleton height={180} className={styles.skeletonCard} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} className={`${styles.gridItem} ${styles.gridItem}`}>
              <Skeleton height={180} className={styles.skeletonCard} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} className={`${styles.gridItem} ${styles.gridItem}`}>
              <Skeleton height={180} className={styles.skeletonCard} />
            </Grid>
          </Grid>

          {/* Date Range Metrics Section Skeleton */}
          <Box className={`${styles.sectionHeader} ${styles.sectionHeaderWithMargin}`}>
            <AssessmentIcon />
            <Typography variant='h6' component='h2' className={`${styles.sectionTitle} ${styles.sectionTitle}`}>
              {t('metrics.sections.dateRangeMetrics')}
            </Typography>
          </Box>
          <Skeleton height={36} width={200} className={styles.skeletonSpacing} />

          {/* Date Range Metrics Cards Skeletons */}
          <Grid container spacing={2} className={`${styles.metricsContainer} ${styles.metricsContainer}`}>
            <Grid item xs={12} sm={6} md={4} className={`${styles.gridItem} ${styles.gridItem}`}>
              <Skeleton height={180} className={styles.skeletonCard} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} className={`${styles.gridItem} ${styles.gridItem}`}>
              <Skeleton height={180} className={styles.skeletonCard} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} className={`${styles.gridItem} ${styles.gridItem}`}>
              <Skeleton height={180} className={styles.skeletonCard} />
            </Grid>
          </Grid>
        </Box>
      ) : metrics === null ? (
        <Alert severity='error'>{t('errors.fetchFailed')}</Alert>
      ) : (
        <>
          {/* Today's Metrics */}
          <div ref={todayMetricsRef} aria-live='polite'>
            {/*  Should always display by default */}
            {isTodaySelected() ? (
              <>
                <div className={`${styles.mainMetricsContainer} animate-in animate-in-3`}>
                  <h2 className={`${styles.sectionHeader} ${styles.sectionHeader}`}>
                    <EventNoteIcon />
                    <span className={`${styles.sectionTitle} ${styles.sectionTitle}`}>
                      {t('metrics.sections.todayMetrics')}
                    </span>
                    <span className={styles.dateRangeInline}>
                      {'— '}{metricsDateRange.startDate.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </h2>

                  <Grid container spacing={1} className={`${styles.metricsContainer} ${styles.metricsContainer}`}>
                    {/* Daily Call Count */}
                    <Grid item xs={12} sm={4} className={`${styles.gridItem} ${styles.gridItem}`}>
                      {dateRangeLoading
                        ? (
                          <Skeleton height={180} className={styles.skeletonCard} />
                          )
                        : (
                          <MetricCard
                            icon={PhoneIcon}
                            value={metricsData.dailyCallCount ?? 0}
                            label={t('metrics.titles.dailyCallCount')}
                            description={t('metrics.descriptions.dailyCallCount')}
                            type='daily'
                            className='metric-card'
                            prevValue={metricsData.prevDayCallCount}
                            avgValue={metricsData.avgDailyCalls}
                            workdaysLabel={metricsData.workdaysLabel}
                          />
                          )}
                    </Grid>

                    {/* Daily Call Minutes */}
                    <Grid item xs={12} sm={4} className={`${styles.gridItem} ${styles.gridItem}`}>
                      {dateRangeLoading
                        ? (
                          <Skeleton height={180} className={styles.skeletonCard} />
                          )
                        : (
                          <MetricCard
                            icon={AccessTimeIcon}
                            value={metricsData.dailyWorkMinutes ?? 0}
                            label={t('metrics.titles.dailyCallMinutes')}
                            description={t('metrics.descriptions.dailyCallMinutes')}
                            type='daily'
                            className='metric-card'
                            prevValue={metricsData.prevDayWorkMinutes}
                            avgValue={metricsData.avgDailyMinutes}
                            workdaysLabel={metricsData.workdaysLabel}
                          />
                          )}
                    </Grid>

                    {/* Hot Lead (Potential New Clients) */}
                    <Grid item xs={12} sm={4} className={`${styles.gridItem} ${styles.gridItem}`}>
                      {dateRangeLoading
                        ? (
                          <Skeleton height={180} className={styles.skeletonCard} />
                          )
                        : (
                          <MetricCard
                            icon={PersonAddIcon}
                            value={metricsData.hotLeads ?? 0}
                            label={t('metrics.titles.hotLeads')}
                            description={t('metrics.descriptions.hotLeads')}
                            type='daily'
                            className='metric-card'
                            prevValue={metricsData.prevDayHotLeads}
                            avgValue={metricsData.avgDailyHotLeads}
                            workdaysLabel={metricsData.workdaysLabel}
                          />
                          )}
                    </Grid>
                  </Grid>
                </div>
              </>
            )
              : <>
                <div ref={dateRangeMetricsRef}>
                  <div className={`${styles.mainMetricsContainer} animate-in animate-in-3`}>
                    <Box className={`${styles.sectionHeader} ${styles.sectionHeader}`}>
                      <AssessmentIcon />
                      <Typography variant='h6' component='h2' className={`${styles.sectionTitle} ${styles.sectionTitle}`}>
                        {t('metrics.sections.dateRangeMetrics')}
                      </Typography>
                    </Box>
                    <Typography
                      className={`${styles.dateRange} ${styles.dateRange}`}
                    >
                      {metricsDateRange.startDate.toDateString() === metricsDateRange.endDate.toDateString()
                        ? metricsDateRange.startDate.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                        : `${metricsDateRange.startDate.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })} \u2014 ${metricsDateRange.endDate.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}`}
                    </Typography>

                    {/* Date Range Picker - moved here from above, only shown when showDatePicker is true */}
                    {/* {showDatePicker && (
              <Box className={styles.dateRangeContainer} style={{ marginTop: '12px', marginBottom: '20px' }}>
                <DateRangeInput
                  startDate={dateRangeStart}
                  endDate={dateRangeEnd}
                  onStartDateChange={onStartDateChange}
                  onEndDateChange={onEndDateChange}
                />
              </Box>
            )} */}

                    <Grid container spacing={1} className={`${styles.metricsContainer} ${styles.metricsContainer}`}>
                      {/* Total Call Count */}
                      <Grid item xs={12} sm={4} className={`${styles.gridItem} ${styles.gridItem}`}>
                        {dateRangeLoading
                          ? (
                            <Skeleton height={180} className={styles.skeletonCard} />
                            )
                          : (
                            <MetricCard
                              icon={PhoneIcon}
                              value={metricsData.callCount ?? 0}
                              label={t('metrics.titles.totalCallCount')}
                              description={t('metrics.descriptions.totalCallCount')}
                              type='dateRange'
                              className='metric-card'
                            />
                            )}
                      </Grid>

                      {/* Total Call Minutes */}
                      <Grid item xs={12} sm={4} className={`${styles.gridItem} ${styles.gridItem}`}>
                        {dateRangeLoading
                          ? (
                            <Skeleton height={180} className={styles.skeletonCard} />
                            )
                          : (
                            <MetricCard
                              icon={AccessTimeIcon}
                              value={metricsData.workMinutes ?? 0}
                              label={t('metrics.titles.totalCallMinutes')}
                              description={t('metrics.descriptions.totalCallMinutes')}
                              type='dateRange'
                              className='metric-card'
                            />
                            )}
                      </Grid>

                      {/* Date Range Potential New Clients */}
                      <Grid item xs={12} sm={4} className={`${styles.gridItem} ${styles.gridItem}`}>
                        {dateRangeLoading
                          ? (
                            <Skeleton height={180} className={styles.skeletonCard} />
                            )
                          : (
                            <MetricCard
                              icon={PersonAddIcon}
                              value={metricsData.dateRangeHotLeads ?? 0}
                              label={t('metrics.titles.hotLeads')}
                              description={t('metrics.descriptions.hotLeads')}
                              type='daily'
                              className='metric-card'
                            />
                            )}
                      </Grid>

                      {/* Client Satisfaction (Date Range) - Hidden for now but keeping code for future use */}
                      {isCssEnabled && (
                        <Grid item xs={12} sm={4} className={`${styles.gridItem} ${styles.gridItem}`}>
                          {dateRangeLoading
                            ? (
                              <Skeleton height={180} className={styles.skeletonCard} />
                              )
                            : (
                              <MetricCard
                                icon={SentimentSatisfiedIcon}
                                value={metricsData.css ?? 0}
                                label={t('metrics.titles.clientSatisfaction')}
                                description={t('metrics.descriptions.clientSatisfaction')}
                                type='dateRange'
                                isPercentage
                                className='metric-card'
                              />
                              )}
                        </Grid>
                      )}
                    </Grid>
                  </div>
                </div>
                </>}

          </div>
        </>
      )}

    </>
  )
}

export default EnhancedMetricsSection
